import React from "react";
import { formatSemestreLabel } from "../../utils/curriculumMalla";
import MallaCourseCard from "./MallaCourseCard";

function MallaGridCell({
  cell,
  gridColumn,
  gridRow,
  rowSpan = 1,
  onSelectMateria,
}) {
  const gridStyle = {
    gridColumn,
    gridRow: `${gridRow} / span ${rowSpan}`,
  };

  if (!cell) {
    return (
      <div
        className="course-detail__materia-cell course-detail__materia-cell--empty"
        style={gridStyle}
        aria-hidden="true"
      />
    );
  }

  const { trayecto, materias } = cell;
  const style = {
    ...gridStyle,
    backgroundColor: trayecto?.color,
    color: trayecto?.textColor,
  };

  return (
    <div
      className={`course-detail__materia-cell${
        onSelectMateria ? " course-detail__materia-cell--interactive" : ""
      }`}
      style={style}
      role="gridcell"
    >
      {materias.map((m) => (
        <MallaCourseCard
          key={m.id}
          materia={m}
          trayecto={trayecto}
          onSelect={onSelectMateria}
          compact
        />
      ))}
    </div>
  );
}

function MallaGrid({
  gridModel,
  mallaTitulo,
  onSelectMateria,
  rowHeaderLabel = "Trayecto",
  showPhaseRow = true,
}) {
  const { columns, rowCount, trayectos, semestres, fases } = gridModel;
  const semCount = semestres.length;
  const matrixMinWidth = Math.max(720, 220 + semCount * 140);

  const columnTemplate = `220px repeat(${semCount}, minmax(132px, 1fr))`;
  const headerGridStyle = { gridTemplateColumns: columnTemplate };
  const bodyGridStyle = {
    gridTemplateColumns: columnTemplate,
    gridTemplateRows: `repeat(${rowCount}, minmax(5.5rem, auto))`,
  };

  return (
    <section
      className="course-detail__malla course-detail__malla--desktop"
      aria-labelledby="malla-curricular-heading"
    >
      <div className="course-detail__malla__header">
        <div className="spacer--red" />
        <h3 id="malla-curricular-heading">{mallaTitulo}</h3>
        <p className="course-detail__malla__hint">
          Desliza horizontalmente para ver la malla completa
        </p>
      </div>

      <div className="course-detail__malla-scroll">
        <div
          className="course-detail__matrix"
          style={{ minWidth: matrixMinWidth }}
          role="grid"
        >
          {showPhaseRow && fases?.length > 0 && (
            <div
              className="course-detail__matrix__phase-row"
              style={headerGridStyle}
              role="row"
            >
              <div
                className="course-detail__matrix__corner course-detail__matrix__corner--phase"
                role="columnheader"
              />
              {fases.map((fase) => (
                <div
                  key={fase.id}
                  className="course-detail__matrix__phase"
                  style={{ gridColumn: `span ${fase.semestres.length}` }}
                  role="columnheader"
                >
                  {fase.label}
                </div>
              ))}
            </div>
          )}

          <div
            className="course-detail__matrix__sem-row"
            style={headerGridStyle}
            role="row"
          >
            <div
              className="course-detail__matrix__corner course-detail__matrix__corner--sem"
              role="columnheader"
            >
              {rowHeaderLabel}
            </div>
            {semestres.map((sem) => (
              <div
                key={sem}
                className="course-detail__matrix__sem-header"
                role="columnheader"
              >
                {formatSemestreLabel(sem)}
              </div>
            ))}
          </div>

          <div
            className="course-detail__matrix__body"
            style={bodyGridStyle}
            role="rowgroup"
          >
            {trayectos.map((t, rowIdx) => (
              <div
                key={t.id}
                className="course-detail__matrix__trayecto-label"
                style={{ gridColumn: 1, gridRow: rowIdx + 1 }}
                role="rowheader"
              >
                {t.label}
              </div>
            ))}

            {semestres.map((sem, colIdx) => {
              const column = columns[sem];
              return column.rows.map((row, rowIdx) => {
                if (row.blocked) return null;
                const span = row.cell?.rowSpan || 1;
                return (
                  <MallaGridCell
                    key={`${sem}-${rowIdx}`}
                    cell={row.cell}
                    gridColumn={colIdx + 2}
                    gridRow={rowIdx + 1}
                    rowSpan={span}
                    onSelectMateria={onSelectMateria}
                  />
                );
              });
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default MallaGrid;
